import { useState } from 'react';
import { Calendar, DollarSign, Users, Plus, X, Trash2/*, Tag*/ } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../useApp';
import type { GiftEvent } from '../store';
import { AdSidebar } from '../components/AdSidebar';
import './Dashboard.css';

function CircularProgress({ percentage, size = 60 }: { percentage: number; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedPct = Math.min(percentage, 100);
  const offset = circumference - (clampedPct / 100) * circumference;
  const color = percentage >= 100 ? '#ef4444' : percentage > 80 ? '#f59e0b' : '#00C950';

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#ECECF0" strokeWidth="6" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke={color} strokeWidth="6" fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="circular-progress-label">
        <span>{Math.round(percentage)}%</span>
      </div>
    </div>
  );
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function formatDate(date: string) {
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

interface EventModalProps {
  onClose: () => void;
  editEvent?: GiftEvent | null;
}

function EventModal({ onClose, editEvent }: EventModalProps) {
  const { contacts, eventTypes, addEvent, updateEvent, deleteEvent/*, addEventType*/ } = useApp();
  const defaultTypeId = eventTypes[0]?.id ?? 1;
  const [name, setName] = useState(editEvent?.name ?? '');
  const [typeId, setTypeId] = useState<number>(editEvent?.type_id ?? defaultTypeId);
  const [date, setDate] = useState(editEvent?.date ?? '');
  const [budget, setBudget] = useState(editEvent ? String(editEvent.budget) : '');
  const [selectedContacts, setSelectedContacts] = useState<string[]>(editEvent?.contactIds ?? []);
  const [notes, setNotes] = useState(editEvent?.notes ?? '');
  const [imageUrl, setImageUrl] = useState(editEvent?.image_url ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Custom type creation state — commented out for now, re-enable when per-user types are implemented
  // const [showCustomType, setShowCustomType] = useState(false);
  // const [customTypeName, setCustomTypeName] = useState('');
  // const [customTypeColor, setCustomTypeColor] = useState('#6366f1');
  // const [creatingType, setCreatingType] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Event name is required';
    else if (name.trim().length > 255) e.name = 'Event name must be 255 characters or less';
    if (!date) e.date = 'Date is required';
    if (budget && (isNaN(Number(budget)) || Number(budget) < 0)) e.budget = 'Budget must be a positive number';
    else if (budget && Number(budget) > 99999999.99) e.budget = 'Budget is too large';
    if (imageUrl && imageUrl.length > 512) e.imageUrl = 'Image URL must be 512 characters or less';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // const handleCreateCustomType = async () => {
  //   if (!customTypeName.trim()) return;
  //   setCreatingType(true);
  //   try {
  //     const newType = await addEventType(customTypeName.trim(), customTypeColor);
  //     setTypeId(newType.id);
  //     setCustomTypeName('');
  //     setShowCustomType(false);
  //     toast.success(`Event type "${newType.name}" created`);
  //   } catch (err) {
  //     toast.error((err as Error).message ?? 'Failed to create event type');
  //   } finally {
  //     setCreatingType(false);
  //   }
  // };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const typeName = eventTypes.find((t) => t.id === typeId)?.name ?? '';
      const payload = {
        name: name.trim(), type: typeName, type_id: typeId,
        date, budget: Number(budget), contactIds: selectedContacts,
        notes, image_url: imageUrl.trim() || undefined,
      };
      if (editEvent) {
        await updateEvent(editEvent.id, payload);
        toast.success('Event updated');
      } else {
        await addEvent(payload);
        toast.success('Event created');
      }
      onClose();
    } catch (err) {
      toast.error((err as Error).message ?? 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editEvent) return;
    setDeleting(true);
    setShowConfirm(false);
    try {
      await deleteEvent(editEvent.id);
      toast.success('Event deleted');
      onClose();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const toggleContact = (id: string) => {
    setSelectedContacts(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{editEvent ? 'Edit Event' : 'Create New Event'}</h2>
            <p className="modal-subtitle">Add a new gift-giving event to track and manage</p>
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Event Name</label>
            <input
              className="form-input"
              placeholder="e.g., Mom's Birthday, Christmas 2026"
              value={name} onChange={e => setName(e.target.value)}
              maxLength={255}
            />
            {errors.name && <p className="form-error">{errors.name}</p>}
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Event Type</label>
              <select className="form-select" value={typeId} onChange={e => setTypeId(Number(e.target.value))}>
                {eventTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {/* Custom type creation — commented out for now
              <button
                type="button"
                className="btn-create-type"
                onClick={() => setShowCustomType(v => !v)}
              >
                <Tag size={12} />
                {showCustomType ? 'Cancel' : 'Create custom type'}
              </button>
              {showCustomType && (
                <div className="custom-type-row">
                  <input
                    className="form-input"
                    placeholder="Type name"
                    value={customTypeName}
                    onChange={e => setCustomTypeName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateCustomType()}
                  />
                  <input
                    type="color"
                    className="color-picker"
                    value={customTypeColor}
                    onChange={e => setCustomTypeColor(e.target.value)}
                    title="Pick a color"
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleCreateCustomType}
                    disabled={creatingType || !customTypeName.trim()}
                  >
                    {creatingType ? '…' : 'Add'}
                  </button>
                </div>
              )}
              */}
            </div>
            <div className="form-group">
              <label className="form-label">Event Date</label>
              <input
                type="date"
                className="form-input-date"
                value={date} onChange={e => setDate(e.target.value)}
              />
              {errors.date && <p className="form-error">{errors.date}</p>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Total Budget <span className="form-label-optional">(Optional)</span></label>
            <div className="form-input-prefix">
              <span className="form-input-prefix-symbol">$</span>
              <input
                type="number" min="0" step="0.01"
                className="form-input"
                placeholder="0.00"
                value={budget} onChange={e => setBudget(e.target.value)}
              />
            </div>
            {errors.budget && <p className="form-error">{errors.budget}</p>}
          </div>
          <div className="form-group">
            <label className="form-label">Gift Recipients</label>
            <div className="recipient-picker">
              {contacts.map(c => (
                <button
                  key={c.id}
                  onClick={() => toggleContact(c.id)}
                  className={`recipient-btn${selectedContacts.includes(c.id) ? ' selected' : ''}`}
                >
                  {c.name}
                </button>
              ))}
              {contacts.length === 0 && <span style={{ fontSize: 14, color: '#717182' }}>No contacts yet</span>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Image URL (Optional)</label>
            <input
              type="url"
              className="form-input"
              placeholder="https://..."
              value={imageUrl} onChange={e => setImageUrl(e.target.value)}
              maxLength={512}
            />
            {errors.imageUrl && <p className="form-error">{errors.imageUrl}</p>}
          </div>
          <div className="form-group">
            <label className="form-label">Notes (Optional)</label>
            <textarea
              className="form-textarea"
              placeholder="Add any additional notes or gift ideas..."
              rows={3}
              value={notes} onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-footer">
          {editEvent && (
            <button className="btn-danger-sm" onClick={() => setShowConfirm(true)} disabled={deleting || submitting}>
              <Trash2 size={13} />
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <div className="modal-footer-actions">
            <button className="btn-cancel" onClick={onClose} disabled={submitting || deleting}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={submitting || deleting}>
              {submitting ? 'Saving…' : editEvent ? 'Save Changes' : 'Create Event'}
            </button>
          </div>
        </div>
      </div>
      {showConfirm && (
        <div className="confirm-overlay" onClick={() => setShowConfirm(false)}>
          <div className="confirm-box" onClick={e => e.stopPropagation()}>
            <h3 className="confirm-title">Confirm Delete</h3>
            <p className="confirm-message">Delete "{editEvent?.name}"? This will also remove all associated gift ideas.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { events, contacts, eventTypes, getSpentForEvent } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<GiftEvent | null>(null);

  const today = new Date().toLocaleDateString('en-CA');
  const upcoming = [...events].filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = [...events].filter(e => e.date < today).sort((a, b) => b.date.localeCompare(a.date));

  const renderCard = (event: GiftEvent) => {
    const spent = getSpentForEvent(event.id);
    const pct = event.budget > 0 ? (spent / event.budget) * 100 : 0;
    const eventContacts = contacts.filter(c => event.contactIds.includes(c.id));
    const shown = eventContacts.slice(0, 2);
    const extra = eventContacts.length - shown.length;
    const spentClass = spent >= event.budget ? 'over' : spent / event.budget >= 0.8 ? 'warning' : '';
    const eventType = eventTypes.find(t => t.id === event.type_id);
    const eventImage = event.image_url ?? 'https://images.unsplash.com/photo-1651399973942-1721a0de0851?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600';
    const badgeColor = eventType?.color ?? '#94a3b8';

    return (
      <div
        key={event.id}
        className="event-card"
        onClick={() => { setEditingEvent(event); setShowModal(true); }}
      >
        <div className="event-card-image">
          <img src={eventImage} alt={event.type} />
          <div className="event-card-image-overlay" />
          <span className="event-type-badge" style={{ backgroundColor: badgeColor }}>{event.type}</span>
        </div>
        <div className="event-card-body">
          <div className="event-card-title-row">
            <div className="event-card-title-wrap">
              <h3 className="event-card-name">{event.name}</h3>
              <div className="event-card-date">
                <Calendar size={13} />
                <span>{formatDate(event.date)}</span>
              </div>
            </div>
            <CircularProgress percentage={pct} size={56} />
          </div>
          <div className="event-card-stats">
            <div className="event-stat-row">
              <div className="event-stat-label">
                <DollarSign size={14} />
                <span>Budget</span>
              </div>
              <span className="event-stat-value">
                <span className={`budget-spent${spentClass ? ` ${spentClass}` : ''}`}>${spent.toFixed(2)}</span>
                <span className="budget-total"> / ${event.budget.toFixed(2)}</span>
              </span>
            </div>
            <div className="event-stat-label">
              <Users size={14} />
              <span>Contacts</span>
            </div>
            <div className="contact-chips">
              {shown.map(c => (
                <span key={c.id} className="contact-chip">
                  <span className="contact-chip-avatar">{getInitials(c.name)}</span>
                  <span className="contact-chip-name">{c.name}</span>
                </span>
              ))}
              {extra > 0 && <span className="contact-chip-more">+{extra} more</span>}
              {eventContacts.length === 0 && <span style={{ fontSize: 13, color: '#717182' }}>No recipients</span>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-page">
      <AdSidebar side="left" />
      <AdSidebar side="right" />
      <div className="dashboard-inner">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Event Dashboard</h1>
            <p className="dashboard-subtitle">Track your gift-giving events and stay within budget</p>
          </div>
          <button className="btn-add" onClick={() => { setEditingEvent(null); setShowModal(true); }}>
            <Plus size={14} />
            Add Event
          </button>
        </div>

        {events.length === 0 ? (
          <div className="dashboard-empty">
            <p>No events yet :(</p>
            <p>Click "+ Add Event" to get started</p>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <div className="dashboard-section">
                <h2 className="dashboard-section-heading">Upcoming Events</h2>
                <div className="event-grid">{upcoming.map(renderCard)}</div>
              </div>
            )}
            {past.length > 0 && (
              <div className="dashboard-section">
                <h2 className="dashboard-section-heading">Past Events</h2>
                <div className="event-grid">{past.map(renderCard)}</div>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <EventModal
          onClose={() => { setShowModal(false); setEditingEvent(null); }}
          editEvent={editingEvent}
        />
      )}
    </div>
  );
}
